import { useEffect, useRef, useState } from "react";
import { Canceler } from "axios";
import { useTrans } from "../../../hooks";
import { HStack, useToast, Box, VStack } from "@chakra-ui/react";
import {
    AppFilterButton,
    AppPageContent,
    AppPageHeader,
} from "../../../components";
import { FindInternshipSchoolFilter as FilterComponent } from "./FindInternshipSchoolFilter";
import {
    Internship as Entity,
    InternshipApi as EntityApi,
    Partnership,
    PartnershipApi,
} from "../../../apis";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC } from "../../../config";
import { AppFindSchoolInternshipMap } from "./AppFindSchoolInternshipMap";

export const FindInternshipSchoolPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);

    // state & const
    const [partnershipStatusList, setPartnershipStatusList] = useState<
        Record<string, Partnership>
    >({});
    const [filterData, setFilterData] = useState<Record<string, any>>({
        includeAvailableSeats: true,
    });
    const [list, setList] = useState<Entity[]>([]);
    const [mapCenter, setMapCenter] = useState<[number, number]>([
        48.8575475, 2.3513765,
    ]);
    const pageSize = APPC.PAGE_SIZES[1];

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    // handler
    const fetchListData = () => {
        const params = {
            itemsPerPage: pageSize,
            xLatitude: mapCenter[0],
            xLongitude: mapCenter[1],
            xRadius: 100000,
            "order[name]": "asc",
            ...filterData,
        };

        EntityApi.getCollectionFindForMap<Entity>(1, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        }).then(({ errorMessage, response }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                setList(response.items);
            }
        });
    };

    const fetchPartnershipStatus = () => {
        const params = {
            pagination: false,
            ["school.id"]: activeSchoolOrCompany?.["@id"] ?? "",
        };

        PartnershipApi.getCollectionMyPartnershipsStatus<Partnership>(
            1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        ).then(({ errorMessage, response }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                const transformedStatus: Record<string, Partnership> = {};
                response.items.forEach((item) => {
                    const companyId =
                        String(item?.company?.["@id"]).split("/").pop() || "";
                    transformedStatus[companyId] = item;
                });
                setPartnershipStatusList(transformedStatus);
            }
        });
    };

    // useEffect
    useEffect(() => {
        fetchPartnershipStatus();
        fetchListData();
    }, [pageSize, filterData, mapCenter]);

    useEffect(() => {
        const lat = Number(activeSchoolOrCompany?.latitude);
        const lng = Number(activeSchoolOrCompany?.longitude);

        if (lat && lng) {
            setMapCenter([lat, lng]);
        }
    }, [activeSchoolOrCompany]);

    return (
        <>
            <AppPageHeader
                title={t("padm.FindInternshipSchoolPage:text.pageTitle")}
            >
                <HStack gap={4} py={{ base: 3.5, sm: 4, md: "1.125rem" }}>
                    <FilterComponent
                        filterData={filterData}
                        setFilterData={setFilterData}
                    >
                        <Box role="button">
                            <AppFilterButton filterData={filterData} />
                        </Box>
                    </FilterComponent>
                </HStack>
            </AppPageHeader>
            <AppPageContent>
                <VStack
                    p={6}
                    gap={6}
                    alignItems="stretch"
                    justifyContent="stretch"
                    h={"100%"}
                    minH={"400px"}
                >
                    <AppFindSchoolInternshipMap
                        list={list}
                        partnershipStatusList={partnershipStatusList}
                        activeSchoolOrCompany={activeSchoolOrCompany}
                        onCenterChange={(lat, lng) => {
                            setMapCenter((prev) => {
                                if (
                                    Math.abs(prev[0] - lat) < 0.002 &&
                                    Math.abs(prev[1] - lng) < 0.002
                                ) {
                                    return prev;
                                }
                                return [lat, lng];
                            });
                        }}
                        mapProps={{ center: mapCenter }}
                    />
                </VStack>
            </AppPageContent>
        </>
    );
};
